import { useMemo } from 'react'
import { useGetAssetsQuery } from '~/store/api'

const ASSET_PREFIX = 'asset:'

let _store = null

export function initAssetResolver(store) {
	_store = store
}

export function isAssetRef(source) {
	return typeof source === 'string' && source.startsWith(ASSET_PREFIX)
}

export function parseAssetId(source) {
	if (!isAssetRef(source)) return null
	return source.slice(ASSET_PREFIX.length)
}

/**
 * Resolve an image source string to a displayable URL.
 * - "asset:{id}" → look up in assets array, return mediaUrl
 * - blob:/https:/data: → pass through
 * - empty/null → null
 */
export function resolveAssetUrl(source, assets) {
	if (!source) return null
	if (!isAssetRef(source)) return source

	const id = source.slice(ASSET_PREFIX.length)
	const asset = assets?.find(a => a.id === id)
	return asset?.mediaUrl || null
}

/**
 * Sync version that reads directly from the Redux store. Use in non-React code (effects, thunks).
 */
export function resolveAssetUrlSync(source) {
	if (!source) return null
	if (!isAssetRef(source)) return source

	if (!_store) return null
	const state = _store.getState()
	const assets = state.api.queries?.['getAssets(undefined)']?.data
	return resolveAssetUrl(source, assets)
}

/** ⚠️ `data` alone cannot say: it is undefined both before the query runs and after it fails, and those two need opposite handling. */
export function assetsLoaded() {
	if (!_store) return false
	return _store.getState().api.queries?.['getAssets(undefined)']?.status === 'fulfilled'
}

/**
 * Hook for React components. Returns the resolved display URL for any image source.
 */
export function useResolvedAssetUrl(source) {
	const { data: assets, isLoading } = useGetAssetsQuery()

	const url = useMemo(() => resolveAssetUrl(source, assets), [source, assets])

	return { url, loading: isAssetRef(source) && isLoading }
}
